/**
 * Replay List Component
 * Displays list of available game replays in table format
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './ReplayList.css';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || '';

function ReplayList({ twitterUser }) {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Check if current user is _nngmi
  const isAdmin = twitterUser && twitterUser.screen_name === '_nngmi';
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });
  const [filter, setFilter] = useState({
    level_id: '',
    player_name: ''
  });
  const [sort, setSort] = useState({
    sortBy: 'date',
    sortOrder: 'desc'
  });

  useEffect(() => {
    loadReplays();
  }, [filter, sort, pagination.page]);

  const loadReplays = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('limit', pagination.limit);
      params.append('sort_by', sort.sortBy);
      params.append('sort_order', sort.sortOrder);

      if (filter.level_id) {
        params.append('level_id', filter.level_id);
      }
      if (filter.player_name) {
        params.append('player_name', filter.player_name);
      }

      // If admin, send twitter_user to get all replays including private
      if (isAdmin) {
        params.append('twitter_user', twitterUser.screen_name);
      }

      const response = await fetch(`${SERVER_URL}/api/replay/sessions?${params}`);

      if (!response.ok) {
        throw new Error('Failed to load replays');
      }

      const data = await response.json();
      setSessions(data.sessions || []);
      setPagination(data.pagination || {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
      });
      setLoading(false);
    } catch (err) {
      console.error('Error loading replays:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const handleSort = (newSortBy) => {
    setSort(prevSort => ({
      sortBy: newSortBy,
      sortOrder: prevSort.sortBy === newSortBy && prevSort.sortOrder === 'desc' ? 'asc' : 'desc'
    }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  const formatDuration = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${seconds}s`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getLevelDisplayName = (levelId, levelName) => {
    if (levelId === 'nakamigo_crush') return 'Pixel Crush';
    return levelName;
  };

  const getSortIcon = (column) => {
    if (sort.sortBy !== column) return '↕';
    return sort.sortOrder === 'desc' ? '↓' : '↑';
  };

  // Delete replay (admin only)
  const handleDelete = async (sessionId, e) => {
    e.stopPropagation();
    if (!isAdmin) return;

    const confirmed = window.confirm('Are you sure you want to delete this replay? This action cannot be undone.');
    if (!confirmed) return;

    setDeletingId(sessionId);
    try {
      const response = await fetch(`${SERVER_URL}/api/replay/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          twitter_user: twitterUser.screen_name
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete replay');
      }

      // Reload the replay list after successful deletion
      loadReplays();
    } catch (error) {
      console.error('Error deleting replay:', error);
      alert(`Failed to delete replay: ${error.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  if (loading && sessions.length === 0) {
    return (
      <div className="replay-list loading">
        <h2>Loading replays...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div className="replay-list error">
        <h2>Error loading replays</h2>
        <p>{error}</p>
        <button onClick={loadReplays}>Retry</button>
      </div>
    );
  }

  return (
    <div className="leaderboard-container">
      <table>
        <thead>
          <tr>
            <th colSpan={isAdmin ? "9" : "7"}>
              <h1 className="leaderboard-title">Game Replays</h1>
              <div className="level-buttons">
                <button
                  onClick={() => navigate('/')}
                  className="nav-button level-button"
                  style={{ backgroundColor: 'darkred', color: 'white' }}
                >
                  Home
                </button>
                <button
                  onClick={() => navigate('/mfersbeheaded')}
                  className="level-button"
                  style={{ backgroundColor: '#6F99F8', color: 'black' }}
                >
                  Play Game
                </button>
              </div>

              {/* Filters */}
              <div className="replay-filters">
                <input
                  type="text"
                  placeholder="Search by player name..."
                  value={filter.player_name}
                  onChange={(e) => handleFilterChange({ ...filter, player_name: e.target.value })}
                  className="filter-input"
                />
                <select
                  value={filter.level_id}
                  onChange={(e) => handleFilterChange({ ...filter, level_id: e.target.value })}
                  className="filter-select"
                >
                  <option value="">All Levels</option>
                  <option value="nakamigo_crush">Pixel Crush</option>
                  <option value="house_songs">House Songs</option>
                  <option value="tinydinos">Let's F'ing Rawr</option>
                  <option value="p2p_mfer_system">Mfers Jus Getting Started</option>
                  <option value="book_of_bee">Lil Mfers Playground</option>
                </select>
              </div>
            </th>
          </tr>
          <tr>
            <th>Player</th>
            <th
              onClick={() => handleSort('score')}
              className="sortable"
              style={{ cursor: 'pointer' }}
            >
              Score {getSortIcon('score')}
            </th>
            <th>Moves</th>
            <th>Level</th>
            <th>Duration</th>
            <th
              onClick={() => handleSort('date')}
              className="sortable"
              style={{ cursor: 'pointer' }}
            >
              Date {getSortIcon('date')}
            </th>
            <th>Status</th>
            {isAdmin && <th>Visibility</th>}
            {isAdmin && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {sessions.length === 0 ? (
            <tr>
              <td colSpan={isAdmin ? "9" : "7"} style={{ textAlign: 'center', padding: '40px' }}>
                <p>No replays found.</p>
                <button
                  onClick={() => navigate('/mfersbeheaded')}
                  style={{
                    marginTop: '10px',
                    padding: '10px 20px',
                    backgroundColor: '#6F99F8',
                    color: 'black',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer'
                  }}
                >
                  Play to Create Replays
                </button>
              </td>
            </tr>
          ) : (
            sessions.map((session) => (
              <tr
                key={session.id}
                className="replay-row"
              >
                <td>
                  {session.twitter_user ? (
                    <>
                      {session.twitter_user.profile_image_url_https && (
                        <img
                          src={session.twitter_user.profile_image_url_https}
                          alt={session.twitter_user.screen_name}
                          style={{
                            width: '30px',
                            height: '30px',
                            borderRadius: '50%',
                            marginRight: '8px',
                            verticalAlign: 'middle'
                          }}
                        />
                      )}
                      <span>{session.twitter_user.screen_name}</span>
                    </>
                  ) : (
                    <span>{session.player_name || 'Anonymous'}</span>
                  )}
                </td>
                <td style={{ fontWeight: 'bold' }}>{session.final_score || 0}</td>
                <td>{session.final_moves || 0}</td>
                <td>{getLevelDisplayName(session.level_id, session.level_name)}</td>
                <td>{formatDuration(session.duration_ms)}</td>
                <td>{formatDate(session.created_at)}</td>
                <td>
                  {session.game_state === 1 ? (
                    <div className="live-indicator-container">
                      <span className="live-indicator">LIVE</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/mfersbeheaded/replay/${session.id}`);
                        }}
                        className="watch-button live-watch"
                      >
                        Watch
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/mfersbeheaded/replay/${session.id}`);
                      }}
                      className="watch-button"
                    >
                      Watch
                    </button>
                  )}
                </td>
                {isAdmin && (
                  <td>
                    <span
                      style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '0.85em',
                        fontWeight: 'bold',
                        backgroundColor: session.is_public ? '#27ae60' : '#95a5a6',
                        color: 'white'
                      }}
                    >
                      {session.is_public ? 'Public' : 'Private'}
                    </span>
                  </td>
                )}
                {isAdmin && (
                  <td>
                    <button
                      onClick={(e) => handleDelete(session.id, e)}
                      disabled={deletingId === session.id}
                      className="watch-button"
                      style={{
                        backgroundColor: '#e74c3c',
                        opacity: deletingId === session.id ? 0.5 : 1,
                        minWidth: '60px'
                      }}
                    >
                      {deletingId === session.id ? '...' : '🗑️'}
                    </button>
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={isAdmin ? "9" : "7"}>
              <div className="pagination-controls">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={!pagination.hasPrev || loading}
                  className="pagination-button"
                >
                  ← Previous
                </button>
                <span className="pagination-info">
                  Page {pagination.page} of {pagination.totalPages}
                  {' '}({pagination.total} total replays)
                </span>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={!pagination.hasNext || loading}
                  className="pagination-button"
                >
                  Next →
                </button>
              </div>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

export default ReplayList;
